import { caminhoDaRota } from "./navegacaoMapa";
import type { GrupoDeNavegacao } from "./navegacaoMapa";

/**
 * O que a pessoa RECOLHEU, e como isso sobrevive ao recarregamento.
 *
 * <p>Saiu de `Navegacao.tsx` (ADR-0013 §2) e é a única parte dela que fala com
 * o navegador. Separada, ela é testável sem desenhar nada — e é a parte cujo
 * defeito custaria caro: preferência velha escondendo um destino é destino que
 * a pessoa deixa de saber que existe.
 */

/**
 * Preferência de interface, e só isso.
 *
 * <p>Prefixada porque o domínio é compartilhado com o que mais estiver no
 * `plantus.redearkadia.com.br`, e chave curta é chave que colide.
 *
 * <p><b>Guarda quem está RECOLHIDO, não quem está aberto.</b> A diferença
 * aparece no dia em que um setor novo entra no mapa: com a lista dos abertos,
 * ele não estaria nela e nasceria escondido — módulo novo invisível, sem nada
 * na tela dizendo que existe. Guardando os recolhidos, o desconhecido nasce
 * aberto, e o pior que uma preferência velha faz é reabrir algo que a pessoa
 * tinha fechado. <b>Vale igual para o terceiro nível</b>: submódulo novo
 * dentro de um módulo já conhecido nasce à vista.
 *
 * <p><b>O formato continua uma lista de textos</b>, agora de CAMINHOS:
 * `"producao"` para o setor, `"producao/materias-primas"` para o módulo. O
 * valor antigo — só ids de setor — é um caso particular do novo e continua
 * valendo <b>sem migração nenhuma</b>: quem tinha "Produção" recolhida
 * continua com ela recolhida depois desta versão. Foi por isso que a chave não
 * mudou de nome, apesar de "setores-recolhidos" ter ficado mais estreito que o
 * conteúdo: renomear custaria uma chave órfã no navegador de todo mundo que já
 * usa o sistema, mais um caminho de migração para errar — e compraria só uma
 * palavra melhor.
 */
export const CHAVE_RECOLHIDOS = "plantus.navegacao.setores-recolhidos";

export function lerRecolhidos(): ReadonlySet<string> {
  try {
    const guardado = window.localStorage.getItem(CHAVE_RECOLHIDOS);
    if (guardado === null) return new Set();

    const lido: unknown = JSON.parse(guardado);
    // Valor estragado — mão humana, versão antiga, outra aba — não derruba a
    // navegação: o pior que pode acontecer é a gaveta voltar ao padrão.
    if (!Array.isArray(lido)) return new Set();

    return new Set(lido.filter((valor): valor is string => typeof valor === "string"));
  } catch {
    // `localStorage` LANÇA quando o armazenamento está desligado — navegação
    // privada, política do aparelho. Preferência de interface não vale uma
    // tela em branco.
    return new Set();
  }
}

export function gravarRecolhidos(recolhidos: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(CHAVE_RECOLHIDOS, JSON.stringify([...recolhidos]));
  } catch {
    // Sem armazenamento, a escolha vale só nesta sessão. É o suficiente.
  }
}

/**
 * Tira da lista de recolhidos o caminho INTEIRO da tela atual — setor e módulo.
 *
 * <p>Devolve o mesmo conjunto quando não há o que mudar — é o que permite ao
 * chamador saber, por identidade, se precisa mesmo redesenhar.
 */
export function reabrirOCaminhoDaRota(
  recolhidos: ReadonlySet<string>,
  grupos: readonly GrupoDeNavegacao[],
  rotaAtual: string,
): ReadonlySet<string> {
  const caminho = caminhoDaRota(grupos, rotaAtual).filter((passo) => recolhidos.has(passo));
  if (caminho.length === 0) return recolhidos;

  const proximos = new Set(recolhidos);
  for (const passo of caminho) proximos.delete(passo);
  return proximos;
}
